import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Inbox, Package, Newspaper, LayoutTemplate, Images, Settings, LogOut, Loader2, Plane } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { InboxTab } from "@/components/admin/InboxTab";
import { ProductsTab } from "@/components/admin/ProductsTab";
import { BlogTab } from "@/components/admin/BlogTab";
import { HomeTab } from "@/components/admin/HomeTab";
import { GalleryTab } from "@/components/admin/GalleryTab";
import { SettingsTab } from "@/components/admin/SettingsTab";

const TABS = [
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "products", label: "Products", icon: Package },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "home", label: "Home Page", icon: LayoutTemplate },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("inbox");

  if (user === null)
    return (
      <div className="grid min-h-screen place-items-center bg-aero-deep">
        <Loader2 className="h-6 w-6 animate-spin text-saffron" />
      </div>
    );
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-dashboard" className="flex min-h-screen bg-aero-deep">
      {/* Sidebar */}
      <aside className="flex w-16 shrink-0 flex-col border-r border-steel/20 bg-aero-surface sm:w-60">
        <div className="flex items-center gap-3 border-b border-steel/20 px-4 py-5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-aero-navy text-saffron">
            <Plane className="h-4 w-4" />
          </span>
          <div className="hidden sm:block">
            <p className="font-heading text-sm font-bold leading-tight text-ink">Sharma Model Aero</p>
            <p className="font-body text-[11px] text-steel">Admin Panel</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-2 sm:p-3">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-heading text-sm font-semibold transition-colors ${
                tab === id ? "bg-saffron text-white" : "text-steel hover:bg-aero-navy hover:text-ink"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </nav>
        <div className="space-y-1 border-t border-steel/20 p-2 sm:p-3">
          <Link
            to="/"
            data-testid="admin-view-site-link"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-heading text-sm font-semibold text-steel transition-colors hover:bg-aero-navy hover:text-ink"
          >
            <Plane className="h-4 w-4 shrink-0 rotate-45" />
            <span className="hidden sm:inline">View Website</span>
          </Link>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-heading text-sm font-semibold text-steel transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">Log Out</span>
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="min-w-0 flex-1 overflow-y-auto p-5 sm:p-8">
        {tab === "inbox" && <InboxTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "blog" && <BlogTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "home" && <HomeTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
